import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE_URL = `${BACKEND_URL}/api`;
const SESSION_STORAGE_KEY = "client-value-tracker-session";
const OFFLINE_MUTATION_QUEUE_KEY = "client-value-tracker-offline-mutation-queue";

const getSessionStorageValue = (key) => {
  if (typeof window === "undefined") return "";
  try {
    const sessionRaw = window.sessionStorage.getItem(key);
    if (sessionRaw) return sessionRaw;
    const legacyRaw = window.localStorage.getItem(key);
    if (legacyRaw) {
      window.sessionStorage.setItem(key, legacyRaw);
      window.localStorage.removeItem(key);
      return legacyRaw;
    }
  } catch (_error) {
    // no-op
  }
  return "";
};

const setSessionStorageValue = (key, value) => {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(key, value);
    window.localStorage.removeItem(key);
  } catch (_error) {
    // no-op
  }
};

const removeSessionStorageValue = (key) => {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(key);
    window.localStorage.removeItem(key);
  } catch (_error) {
    // no-op
  }
};

const api = axios.create({
  baseURL: API_BASE_URL,
});

const MUTATION_METHODS = new Set(["post", "patch", "put", "delete"]);
let isReplayingOfflineQueue = false;

const loadOfflineMutationQueue = () => {
  try {
    const raw = getSessionStorageValue(OFFLINE_MUTATION_QUEUE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const persistOfflineMutationQueue = (items) => {
  setSessionStorageValue(OFFLINE_MUTATION_QUEUE_KEY, JSON.stringify(items));
};

const queueOfflineMutation = (config) => {
  const method = String(config?.method || "get").toLowerCase();
  if (!MUTATION_METHODS.has(method)) return;
  if (config?._offlineReplay) return;

  const queue = loadOfflineMutationQueue();
  queue.push({
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    method,
    url: config.url,
    data: config.data,
    params: config.params,
    headers: config.headers,
    queued_at: new Date().toISOString(),
  });
  persistOfflineMutationQueue(queue);
};

const replayOfflineMutationQueue = async () => {
  if (isReplayingOfflineQueue) return;
  if (typeof navigator !== "undefined" && !navigator.onLine) return;

  const queue = loadOfflineMutationQueue();
  if (!queue.length) return;

  isReplayingOfflineQueue = true;
  const remaining = [];

  for (const item of queue) {
    try {
      await api.request({
        method: item.method,
        url: item.url,
        data: item.data,
        params: item.params,
        headers: item.headers,
        _offlineReplay: true,
      });
    } catch (_error) {
      remaining.push(item);
    }
  }

  persistOfflineMutationQueue(remaining);
  isReplayingOfflineQueue = false;
};

if (typeof window !== "undefined") {
  window.addEventListener("online", () => {
    replayOfflineMutationQueue();
  });
  setInterval(() => {
    replayOfflineMutationQueue();
  }, 12000);
}

api.interceptors.request.use((config) => {
  const rawSession = getSessionStorageValue(SESSION_STORAGE_KEY);
  if (!rawSession) {
    return config;
  }

  try {
    const session = JSON.parse(rawSession);
    if (session?.token) {
      config.headers.Authorization = `Bearer ${session.token}`;
    }
  } catch (_error) {
    removeSessionStorageValue(SESSION_STORAGE_KEY);
  }

  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const method = String(error?.config?.method || "get").toLowerCase();
    const isMutation = MUTATION_METHODS.has(method);
    const isNetworkFailure = !error?.response || error?.code === "ERR_NETWORK" || (typeof navigator !== "undefined" && !navigator.onLine);
    if (isMutation && isNetworkFailure) {
      queueOfflineMutation(error?.config || {});
      return Promise.reject(new Error("Connection issue detected. Your action has been queued and will auto-retry."));
    }

    const status = error?.response?.status;
    const detail = String(error?.response?.data?.detail || "").toLowerCase();
    const isAuthFailure =
      status === 401 &&
      (detail.includes("invalid or expired token") ||
        detail.includes("invalid token") ||
        detail.includes("user not found") ||
        detail.includes("missing authorization token"));

    if (isAuthFailure) {
      removeSessionStorageValue(SESSION_STORAGE_KEY);
      if (typeof window !== "undefined" && window.location.pathname !== "/auth") {
        window.location.href = "/auth";
      }
    }

    return Promise.reject(error);
  },
);

const withErrorHandling = async (requestPromise) => {
  try {
    const response = await requestPromise;
    return response.data;
  } catch (error) {
    const detail = error?.response?.data?.detail;
    const detailIsArray = Array.isArray(detail);
    const detailIsObject = detail && typeof detail === "object" && !detailIsArray;

    let message = "";
    if (typeof detail === "string") {
      message = detail;
    } else if (detailIsArray) {
      message = detail
        .map((item) => {
          if (typeof item === "string") return item;
          if (item && typeof item.msg === "string") return item.msg;
          if (item && typeof item.message === "string") return item.message;
          return "";
        })
        .filter(Boolean)
        .join(" · ");
    } else if (detailIsObject) {
      message = detail.message || detail.code || "";
    }

    if (!message) {
      const fallbackMessage = error?.response?.data?.message;
      if (typeof fallbackMessage === "string") {
        message = fallbackMessage;
      }
    }

    if (!message) {
      const errorMessage = error?.message;
      if (typeof errorMessage === "string" && !errorMessage.includes("[object Object]")) {
        message = errorMessage;
      }
    }

    if (!message) {
      message = "Something went wrong. Please try again.";
    }

    const wrappedError = new Error(message);
    if (detailIsObject) {
      wrappedError.meta = detail;
    }
    throw wrappedError;
  }
};

export const getBootstrapStatus = (workspaceDomain) =>
  withErrorHandling(api.get("/auth/bootstrap-status", { params: { workspace_domain: workspaceDomain } }));

export const bootstrapSuperAdmin = (payload) =>
  withErrorHandling(api.post("/auth/bootstrap-super-admin", payload));

export const loginUser = (payload) => withErrorHandling(api.post("/auth/login", payload));

export const exchangeGoogleSession = (payload) => withErrorHandling(api.post("/auth/google/session", payload));

export const getGoogleAuthClientConfig = () => withErrorHandling(api.get("/auth/google/client-config"));

export const exchangeGoogleAccessToken = (payload) =>
  withErrorHandling(api.post("/auth/google/access-token", payload));

export const completeAuthSetup = (payload) => withErrorHandling(api.post("/auth/setup", payload));

export const getAuthMe = () => withErrorHandling(api.get("/auth/me"));

export const getMyProfile = () => withErrorHandling(api.get("/users/me/profile"));

export const updateMyProfile = (payload) => withErrorHandling(api.patch("/users/me/profile", payload));

export const getDashboard = (params = {}) => withErrorHandling(api.get("/dashboard", { params }));

export const getCommandCenterDashboard = (params = {}) =>
  withErrorHandling(api.get("/dashboard/command-center", { params }));

export const getPodMetrics = (params = {}) => withErrorHandling(api.get("/metrics/pods", { params }));

export const getMyTeamMetrics = (params = {}) => withErrorHandling(api.get("/metrics/my-team", { params }));

export const getGamificationDashboard = (params = {}) =>
  withErrorHandling(api.get("/gamification/dashboard", { params }));

export const getGamificationLeaderboard = (params = {}) =>
  withErrorHandling(api.get("/gamification/leaderboard", { params }));

export const getFinanceReceivables = (params = {}) =>
  withErrorHandling(api.get("/finance/receivables", { params }));

export const updateFinanceClientTerms = (clientId, payload) =>
  withErrorHandling(api.patch(`/finance/clients/${encodeURIComponent(clientId)}/terms`, payload));

export const updateFinanceClientInvoiceToggle = (clientId, payload) =>
  withErrorHandling(api.patch(`/finance/clients/${encodeURIComponent(clientId)}/invoice-toggle`, payload));

export const updateFinanceProjectMonthSettings = (projectId, payload) =>
  withErrorHandling(api.patch(`/finance/projects/${encodeURIComponent(projectId)}/month-settings`, payload));

export const financeMarkPaid = (payload) =>
  withErrorHandling(api.post("/finance/mark-paid", payload));

export const financeMarkUnpaid = (payload) =>
  withErrorHandling(api.post("/finance/mark-unpaid", payload));

export const financeSendPaymentReminder = (clientId, month) =>
  withErrorHandling(api.post(`/finance/clients/${encodeURIComponent(clientId)}/remind`, null, { params: { month } }));

export const financeRaiseFixedMilestoneInvoice = (projectId, milestoneId, payload) =>
  withErrorHandling(api.post(`/finance/fixed-projects/${encodeURIComponent(projectId)}/milestones/${encodeURIComponent(milestoneId)}/raise-invoice`, payload));

export const getFixedProjectMilestones = (projectId) =>
  withErrorHandling(api.get(`/finance/fixed-projects/${encodeURIComponent(projectId)}/milestones`));

export const financeAddFixedMilestone = (projectId, payload) =>
  withErrorHandling(api.post(`/finance/fixed-projects/${encodeURIComponent(projectId)}/milestones`, payload));

export const financeMarkFixedMilestoneDelivered = (projectId, milestoneId, payload) =>
  withErrorHandling(api.post(`/finance/fixed-projects/${encodeURIComponent(projectId)}/milestones/${encodeURIComponent(milestoneId)}/mark-delivered`, payload));

export const financeMarkFixedMilestonePaid = (projectId, milestoneId, payload) =>
  withErrorHandling(api.post(`/finance/fixed-projects/${encodeURIComponent(projectId)}/milestones/${encodeURIComponent(milestoneId)}/mark-paid`, payload));

export const markProjectInvoicePaid = (projectId, invoiceId, payload) =>
  withErrorHandling(api.patch(`/projects/${encodeURIComponent(projectId)}/invoices/${encodeURIComponent(invoiceId)}/mark-paid`, payload));

export const getKpiCategories = () => withErrorHandling(api.get("/kpi/config/categories"));

export const updateKpiCategoryManager = (categoryKey, payload) =>
  withErrorHandling(api.patch(`/kpi/config/categories/${encodeURIComponent(categoryKey)}`, payload));

export const getKpiManagerCatalog = (params = {}) => withErrorHandling(api.get("/kpi/manager/catalog", { params }));

export const getKpiManagerOverview = (params = {}) => withErrorHandling(api.get("/kpi/manager/overview", { params }));

export const createKpiManagerBatchSetup = (payload) => withErrorHandling(api.post("/kpi/manager/batch-setup", payload));

export const unlockKpiSubmission = (payload) => withErrorHandling(api.post("/kpi/manager/unlock", payload));

export const getMyKpiTargets = (params = {}) => withErrorHandling(api.get("/kpi/me", { params }));

export const getKpiAssignmentStatus = () => withErrorHandling(api.get("/kpi/me/assignment-status"));

export const updateKpiTargetActual = (targetId, payload) =>
  withErrorHandling(api.patch(`/kpi/targets/${encodeURIComponent(targetId)}/actual`, payload));

export const submitKpiMonth = (payload) => withErrorHandling(api.post("/kpi/me/submit-month", payload));

export const getManagerGoalsOverview = (params = {}) => withErrorHandling(api.get("/manager-goals/overview", { params }));

export const updateManagerGoalsTargets = (payload) => withErrorHandling(api.patch("/manager-goals/targets", payload));

export const createManagerGoalsProspect = (payload) => withErrorHandling(api.post("/manager-goals/prospects", payload));

export const updateManagerGoalsProspect = (prospectId, payload) =>
  withErrorHandling(api.patch(`/manager-goals/prospects/${encodeURIComponent(prospectId)}`, payload));

export const createManagerGoalsLog = (payload) => withErrorHandling(api.post("/manager-goals/logs", payload));

export const markManagerGoalRenewed = (projectId, payload) =>
  withErrorHandling(api.patch(`/manager-goals/renewals/${encodeURIComponent(projectId)}/mark-renewed`, payload));

export const getContractsRenewalsOverview = (params = {}) => withErrorHandling(api.get("/contracts-renewals/overview", { params }));

export const upsertProjectContractRenewal = (projectId, payload) =>
  withErrorHandling(api.patch(`/contracts-renewals/projects/${encodeURIComponent(projectId)}`, payload));

export const getPodDetail = (managerEmail, params = {}) =>
  withErrorHandling(api.get(`/pod-detail/${encodeURIComponent(managerEmail)}`, { params }));

export const createWorkshop = (payload) => withErrorHandling(api.post("/workshops", payload));

export const getWorkshopsOverview = () => withErrorHandling(api.get("/workshops/overview"));

export const deleteWorkshop = (workshopId) =>
  withErrorHandling(api.delete(`/workshops/${encodeURIComponent(workshopId)}`));

export const getMyWorkshopAssignments = () => withErrorHandling(api.get("/workshops/my-assignments"));

export const submitWorkshopAssignment = (assignmentId, payload) =>
  withErrorHandling(api.patch(`/workshop-assignments/${assignmentId}`, payload));

export const removeWorkshopAssignment = (assignmentId) =>
  withErrorHandling(api.delete(`/workshop-assignments/${encodeURIComponent(assignmentId)}`));

export const getWorkshopSubmissions = (workshopId) =>
  withErrorHandling(api.get(`/workshops/${encodeURIComponent(workshopId)}/submissions`));

export const getMyWorkshopAttendance = () => withErrorHandling(api.get("/workshops/my-attendance"));

export const getAttendanceMe = (params = {}) => withErrorHandling(api.get("/attendance/me", { params }));

export const getAttendanceTeam = (params = {}) => withErrorHandling(api.get("/attendance/team", { params }));

export const endDayAttendance = () => withErrorHandling(api.patch("/attendance/end-day"));

export const getAttendanceSummary = (email, params = {}) =>
  withErrorHandling(api.get(`/attendance/summary/${encodeURIComponent(email)}`, { params }));

export const createProjectAdmin = (payload) => withErrorHandling(api.post("/users/project-admins", payload));

export const createSuperAdmin = (payload) => withErrorHandling(api.post("/users/super-admins", payload));

export const getProjectAdmins = () => withErrorHandling(api.get("/users/project-admins"));

export const getOrgStructureUsers = () => withErrorHandling(api.get("/users/org-structure"));

export const createOrgStructureUser = (payload) =>
  withErrorHandling(api.post("/users/org", payload));

export const updateUserOrg = (email, payload) =>
  withErrorHandling(api.patch(`/users/${encodeURIComponent(email)}/org`, payload));

export const deleteOrgStructureUser = (email) =>
  withErrorHandling(api.delete(`/users/${encodeURIComponent(email)}/org`));

export const getClients = (params = {}) => withErrorHandling(api.get("/clients", { params }));

export const getClientById = (clientId) => withErrorHandling(api.get(`/clients/${clientId}`));

export const getClientProjects = (clientId, params = {}) => withErrorHandling(api.get(`/clients/${clientId}/projects`, { params }));

export const createClient = (payload) => withErrorHandling(api.post("/clients", payload));

export const pauseClient = (clientId, payload = {}) => withErrorHandling(api.patch(`/clients/${clientId}/pause`, payload));

export const resumeClient = (clientId, payload = {}) => withErrorHandling(api.patch(`/clients/${clientId}/resume`, payload));

export const archiveClient = (clientId, payload = {}) => withErrorHandling(api.patch(`/clients/${clientId}/archive`, payload));

export const getProjects = (params = {}) => withErrorHandling(api.get("/projects", { params }));

export const getProjectById = (projectId) => withErrorHandling(api.get(`/projects/${projectId}`));

export const createProject = (payload) => withErrorHandling(api.post("/projects", payload));

export const createClientProject = (clientId, payload) => withErrorHandling(api.post(`/clients/${clientId}/projects`, payload));

export const updateProjectVisibility = (projectId, payload) =>
  withErrorHandling(api.put(`/projects/${projectId}/visibility`, payload));

export const updateProjectFinancials = (projectId, payload) =>
  withErrorHandling(api.patch(`/projects/${projectId}/financials`, payload));

export const renameProject = (projectId, payload) =>
  withErrorHandling(api.patch(`/projects/${projectId}/rename`, payload));

export const pauseProject = (projectId, payload = {}) => withErrorHandling(api.patch(`/projects/${projectId}/pause`, payload));

export const resumeProject = (projectId, payload = {}) => withErrorHandling(api.patch(`/projects/${projectId}/resume`, payload));

export const archiveProject = (projectId, payload = {}) => withErrorHandling(api.patch(`/projects/${projectId}/archive`, payload));

export const updateProjectStatus = (projectId, payload) => withErrorHandling(api.patch(`/projects/${projectId}`, payload));

export const createFixedActivityRow = (projectId, payload) => withErrorHandling(api.post(`/projects/${projectId}/fixed-activity-rows`, payload));

export const updateFixedActivityRow = (projectId, rowKey, payload) => withErrorHandling(api.patch(`/projects/${projectId}/fixed-activity-rows/${rowKey}`, payload));

export const deleteFixedActivityRow = (projectId, rowKey) => withErrorHandling(api.delete(`/projects/${projectId}/fixed-activity-rows/${rowKey}`));

export const generateClientPortalLink = (projectId, payload = {}) => withErrorHandling(api.post(`/projects/${projectId}/portal-link`, payload));

export const getProjectMembers = (projectId) =>
  withErrorHandling(api.get(`/projects/${projectId}/members`));

export const addProjectMember = (projectId, payload) =>
  withErrorHandling(api.post(`/projects/${projectId}/members`, payload));

export const updateProjectMember = (projectId, memberId, payload) =>
  withErrorHandling(api.patch(`/projects/${projectId}/members/${memberId}`, payload));

export const deleteProjectMember = (projectId, memberId) =>
  withErrorHandling(api.delete(`/projects/${projectId}/members/${memberId}`));

export const getProjectTasks = (projectId) => withErrorHandling(api.get(`/projects/${projectId}/tasks`));

export const getAllTasks = (params = {}) => withErrorHandling(api.get("/tasks", { params }));

export const createTaskV2 = (payload) => withErrorHandling(api.post("/tasks", payload));

export const updateTaskV2 = (taskId, payload) => withErrorHandling(api.patch(`/tasks/${taskId}`, payload));

export const getTaskRemarks = (taskId) => withErrorHandling(api.get(`/tasks/${taskId}/remarks`));

export const addTaskRemarkV2 = (taskId, payload) => withErrorHandling(api.post(`/tasks/${taskId}/remarks`, payload));

export const deleteTaskV2 = (taskId) => withErrorHandling(api.delete(`/tasks/${taskId}`));

export const getTaskUserSuggestions = (params = {}) =>
  withErrorHandling(api.get("/tasks/suggest-users", { params }));

export const getTaskProjectSuggestions = (params = {}) =>
  withErrorHandling(api.get("/tasks/suggest-projects", { params }));

export const getGlobalSearch = (params = {}) => withErrorHandling(api.get("/search", { params }));

export const createProjectTask = (projectId, payload) =>
  withErrorHandling(api.post(`/projects/${projectId}/tasks`, payload));

export const updateProjectTask = (projectId, taskId, payload) =>
  withErrorHandling(api.put(`/projects/${projectId}/tasks/${taskId}`, payload));

export const addProjectTaskRemark = (projectId, taskId, payload) =>
  withErrorHandling(api.post(`/projects/${projectId}/tasks/${taskId}/remarks`, payload));

export const getProjectReports = (projectId) =>
  withErrorHandling(api.get(`/projects/${projectId}/reports`));

export const createProjectReport = (projectId, payload) =>
  withErrorHandling(api.post(`/projects/${projectId}/reports`, payload));

export const updateProjectReport = (projectId, reportId, payload) =>
  withErrorHandling(api.patch(`/projects/${projectId}/reports/${reportId}`, payload));

export const getProjectClientDashboardScope = (projectId, params = {}) =>
  withErrorHandling(api.get(`/projects/${projectId}/client-dashboard/scope`, { params }));

export const createProjectClientDashboardScopeItem = (projectId, payload, params = {}) =>
  withErrorHandling(api.post(`/projects/${projectId}/client-dashboard/scope`, payload, { params }));

export const updateProjectClientDashboardScopeItem = (projectId, scopeItemId, payload, params = {}) =>
  withErrorHandling(api.patch(`/projects/${projectId}/client-dashboard/scope/${scopeItemId}`, payload, { params }));

export const updateProjectClientDashboardScopeProgress = (projectId, scopeItemId, payload) =>
  withErrorHandling(api.patch(`/projects/${projectId}/client-dashboard/scope/${scopeItemId}/progress`, payload));

export const deleteProjectClientDashboardScopeItem = (projectId, scopeItemId) =>
  withErrorHandling(api.delete(`/projects/${projectId}/client-dashboard/scope/${scopeItemId}`));

export const getProjectClientDashboardTasks = (projectId) =>
  withErrorHandling(api.get(`/projects/${projectId}/client-dashboard/tasks`));

export const createProjectClientDashboardTask = (projectId, payload) =>
  withErrorHandling(api.post(`/projects/${projectId}/client-dashboard/tasks`, payload));

export const updateProjectClientDashboardTask = (projectId, taskId, payload) =>
  withErrorHandling(api.patch(`/projects/${projectId}/client-dashboard/tasks/${taskId}`, payload));

export const deleteProjectClientDashboardTask = (projectId, taskId) =>
  withErrorHandling(api.delete(`/projects/${projectId}/client-dashboard/tasks/${taskId}`));

export const addProjectClientDashboardTaskNote = (projectId, taskId, payload) =>
  withErrorHandling(api.post(`/projects/${projectId}/client-dashboard/tasks/${taskId}/notes`, payload));

export const getProjectInvoices = (projectId) =>
  withErrorHandling(api.get(`/projects/${projectId}/invoices`));

export const createProjectInvoice = (projectId, payload) =>
  withErrorHandling(api.post(`/projects/${projectId}/invoices`, payload));

export const updateProjectInvoice = (projectId, invoiceId, payload) =>
  withErrorHandling(api.patch(`/projects/${projectId}/invoices/${encodeURIComponent(invoiceId)}`, payload));

export const deleteProjectInvoice = (projectId, invoiceId) =>
  withErrorHandling(api.delete(`/projects/${projectId}/invoices/${encodeURIComponent(invoiceId)}`));

export const getProjectMeetingMinutes = (projectId) =>
  withErrorHandling(api.get(`/projects/${projectId}/meeting-minutes`));

export const createProjectMeetingMinute = (projectId, payload) =>
  withErrorHandling(api.post(`/projects/${projectId}/meeting-minutes`, payload));

export const getProjectBriefs = (projectId) =>
  withErrorHandling(api.get(`/projects/${projectId}/briefs`));

export const createProjectBrief = (projectId, payload) =>
  withErrorHandling(api.post(`/projects/${projectId}/briefs`, payload));

export const getProjectWorkspaceLinks = (projectId) =>
  withErrorHandling(api.get(`/projects/${projectId}/workspace-links`));

export const createProjectWorkspaceLink = (projectId, payload) =>
  withErrorHandling(api.post(`/projects/${projectId}/workspace-links`, payload));

export const getProjectLinks = (projectId) => withErrorHandling(api.get(`/projects/${projectId}/links`));

export const createProjectLink = (projectId, payload) =>
  withErrorHandling(api.post(`/projects/${projectId}/links`, payload));

export const updateProjectLink = (projectId, linkId, payload) =>
  withErrorHandling(api.patch(`/projects/${projectId}/links/${linkId}`, payload));

export const renameProjectLinkFolder = (projectId, folderName, payload) =>
  withErrorHandling(api.patch(`/projects/${projectId}/links/folders/${encodeURIComponent(folderName)}`, payload));

export const deleteProjectLink = (projectId, linkId) =>
  withErrorHandling(api.delete(`/projects/${projectId}/links/${linkId}`));

export const getValueTrackerMeta = () => withErrorHandling(api.get("/value-tracker/meta"));

export const getProjectValueTracker = (projectId, month) =>
  withErrorHandling(
    api.get(`/projects/${projectId}/value-tracker`, {
      params: { month },
    }),
  );

export const updateProjectValueActivity = (projectId, payload) =>
  withErrorHandling(api.post(`/projects/${projectId}/value-tracker/activity`, payload));

export const addProjectValueNote = (projectId, payload) =>
  withErrorHandling(api.post(`/projects/${projectId}/value-tracker/note`, payload));

export const getProjectValueLogs = (projectId, month) =>
  withErrorHandling(
    api.get(`/projects/${projectId}/value-tracker/logs`, {
      params: { month },
    }),
  );

export const getProjectCommandCenter = (projectId, month) =>
  withErrorHandling(api.get(`/projects/${projectId}/command-center`, { params: { month } }));

export const updateProjectCommandActivity = (projectId, payload) =>
  withErrorHandling(api.post(`/projects/${projectId}/command-center/activity`, payload));

export const updateProjectCommandSatisfaction = (projectId, payload) =>
  withErrorHandling(api.post(`/projects/${projectId}/command-center/satisfaction`, payload));

export const getProjectIdeas = (projectId) => withErrorHandling(api.get(`/projects/${projectId}/ideas`));

export const createProjectIdea = (projectId, payload) => withErrorHandling(api.post(`/projects/${projectId}/ideas`, payload));

export const getProjectCommunications = (projectId) =>
  withErrorHandling(api.get(`/projects/${projectId}/communications`));

export const createProjectCommunication = (projectId, payload) =>
  withErrorHandling(api.post(`/projects/${projectId}/communications`, payload));

export const getLearningAssignments = (params = {}) =>
  withErrorHandling(api.get("/learning/assignments", { params }));

export const assignLearningCourses = (payload) => withErrorHandling(api.post("/learning/courses", payload));

export const submitLearningProof = (assignmentId, payload) =>
  withErrorHandling(api.patch(`/learning/assignments/${assignmentId}/proof`, payload));

export const verifyLearningProof = (assignmentId, payload = {}) =>
  withErrorHandling(api.patch(`/learning/assignments/${assignmentId}/verify`, payload));

export const updateLearningAssignment = (assignmentId, payload) =>
  withErrorHandling(api.patch(`/learning/assignments/${assignmentId}`, payload));

export const deleteLearningAssignment = (assignmentId) =>
  withErrorHandling(api.delete(`/learning/assignments/${assignmentId}`));

export const getLeaveBalance = (params = {}) => withErrorHandling(api.get("/hr/leave-balance", { params }));

export const getHrMe = () => withErrorHandling(api.get("/hr/me"));

export const getLeaveRequests = (params = {}) => withErrorHandling(api.get("/hr/leave-requests", { params }));

export const applyLeaveRequest = (payload) => withErrorHandling(api.post("/hr/leave-requests", payload));

export const updateLeaveRequest = (requestId, payload) =>
  withErrorHandling(api.patch(`/hr/leave-requests/${requestId}`, payload));

export const deleteLeaveRequest = (requestId) => withErrorHandling(api.delete(`/hr/leave-requests/${requestId}`));

export const getHrHolidays = (params = {}) => withErrorHandling(api.get("/hr/holidays", { params }));

export const getCalendarEvents = (params = {}) => withErrorHandling(api.get("/calendar", { params }));

export const createCalendarEvent = (payload) => withErrorHandling(api.post("/calendar/events", payload));

export const deleteCalendarEvent = (eventId) => withErrorHandling(api.delete(`/calendar/events/${eventId}`));

export const updateCalendarEvent = (eventId, payload) => withErrorHandling(api.patch(`/calendar/events/${eventId}`, payload));

export const getHrTeamStatus = (params = {}) => withErrorHandling(api.get("/hr/team-status", { params }));

export const getHrDocLinks = () => withErrorHandling(api.get("/hr/doc-links"));

export const createHrDocLink = (payload) => withErrorHandling(api.post("/hr/doc-links", payload));

export const updateHrDocLink = (linkId, payload) => withErrorHandling(api.patch(`/hr/doc-links/${linkId}`, payload));

export const deleteHrDocLink = (linkId) => withErrorHandling(api.delete(`/hr/doc-links/${linkId}`));

export const getHrTeamDirectory = () => withErrorHandling(api.get("/hr/team-directory"));

export const getHrTeamMemberStats = (memberEmail) =>
  withErrorHandling(api.get(`/hr/team-directory/member-stats/${encodeURIComponent(memberEmail)}`));

export const createHrTeamDirectoryMember = (payload) =>
  withErrorHandling(api.post("/hr/team-directory", payload));

export const updateHrTeamDirectoryMember = (memberId, payload) =>
  withErrorHandling(api.patch(`/hr/team-directory/${memberId}`, payload));

export const updateUserByEmail = (email, payload) =>
  withErrorHandling(api.patch(`/users/${encodeURIComponent(email)}`, payload));

export const createRoleRequest = (payload) => withErrorHandling(api.post("/role-requests", payload));

export const getRoleRequests = (params = {}) => withErrorHandling(api.get("/role-requests", { params }));

export const reviewRoleRequest = (requestId, payload) =>
  withErrorHandling(api.patch(`/role-requests/${requestId}`, payload));

export const getNotifications = (params = {}) => withErrorHandling(api.get("/notifications", { params }));

export const markNotificationsRead = (payload = {}) => withErrorHandling(api.patch("/notifications/read", payload));

export const getAuditLogs = (params = {}) => withErrorHandling(api.get("/audit/logs", { params }));

export const getClientPortalMe = (token, deviceId) =>
  withErrorHandling(api.get("/portal/me", { params: { token, device_id: deviceId } }));

export const getClientPortalProjectDetail = (projectId, token, month, deviceId) =>
  withErrorHandling(api.get(`/portal/projects/${projectId}`, { params: { token, month, device_id: deviceId } }));

export const getClientPortalRequests = (token, projectId, deviceId) =>
  withErrorHandling(api.get("/portal/requests", { params: { token, project_id: projectId, device_id: deviceId } }));

export const createClientPortalRequest = (payload, token, deviceId) =>
  withErrorHandling(api.post("/portal/requests", payload, { params: { token, device_id: deviceId } }));

export const updateClientPortalRequest = (requestId, payload, token, deviceId) =>
  withErrorHandling(api.patch(`/portal/requests/${requestId}`, payload, { params: { token, device_id: deviceId } }));

export const deleteClientPortalRequest = (requestId, token, deviceId) =>
  withErrorHandling(api.delete(`/portal/requests/${requestId}`, { params: { token, device_id: deviceId } }));

export const addClientPortalRequestNote = (requestId, payload, token, deviceId) =>
  withErrorHandling(api.post(`/portal/requests/${requestId}/notes`, payload, { params: { token, device_id: deviceId } }));

export const updateClientPortalRequestStatus = (requestId, payload, token, deviceId) =>
  withErrorHandling(api.patch(`/portal/requests/${requestId}/status`, payload, { params: { token, device_id: deviceId } }));

export const getClientPortalTeam = (token, deviceId) =>
  withErrorHandling(api.get("/portal/team", { params: { token, device_id: deviceId } }));

export const getClientDashboardTeamConfig = (clientId) =>
  withErrorHandling(api.get(`/clients/${clientId}/portal-team`));

export const createClientDashboardExternalMember = (clientId, payload) =>
  withErrorHandling(api.post(`/clients/${clientId}/portal-team/external`, payload));

export const updateClientDashboardExternalMember = (clientId, memberId, payload) =>
  withErrorHandling(api.patch(`/clients/${clientId}/portal-team/external/${memberId}`, payload));

export const deleteClientDashboardExternalMember = (clientId, memberId) =>
  withErrorHandling(api.delete(`/clients/${clientId}/portal-team/external/${memberId}`));

export const getClientPortalBilling = (token, deviceId) =>
  withErrorHandling(api.get("/portal/billing", { params: { token, device_id: deviceId } }));

export const connectGoogleWorkspace = (mode = "preview") =>
  withErrorHandling(api.get("/integrations/google/connect", { params: { mode } }));

export const getGoogleWorkspaceStatus = () =>
  withErrorHandling(api.get("/integrations/google/status"));

export const getGoogleWorkspaceFiles = (q = "") =>
  withErrorHandling(api.get("/integrations/google/files", { params: { q: q || undefined } }));

export { SESSION_STORAGE_KEY };
